import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Card, CardContent, Tabs, Tab, Avatar,
  CircularProgress, Alert
} from '@mui/material';
import { Visibility, Check, Close, Search, FilterList, Download, Refresh, DeleteOutline } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/rojgar';

const ProviderKYC = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', provider: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, provider: null });
  const [rejectReason, setRejectReason] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  const rejectReasons = [
    'Invalid Documents',
    'Incomplete Information',
    'Document Quality Poor',
    'Mismatch in Details',
    'Fake Documents',
    'Other'
  ];

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/stats`);
      if (res.data.success) {
        setStats({
          total: res.data.data.totalProviders || 0,
          pending: res.data.data.pendingKYC || 0,
          approved: res.data.data.totalProviders - res.data.data.pendingKYC || 0,
          rejected: 0
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchProviders = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (statusFilter !== 'all') params.kycStatus = statusFilter;
      const res = await axios.get(`${API_BASE}/admin/providers`, { params });
      if (res.data.success) {
        setProviders(res.data.data);
        setTotal(res.data.pagination?.total || res.data.data.length);
      }
    } catch (err) {
      setError('Error fetching providers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchProviders();
  }, [page, rowsPerPage, statusFilter]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'submitted':
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const handleApprove = (provider) => {
    setActionDialog({ open: true, type: 'approve', provider });
  };

  const handleReject = (provider) => {
    setActionDialog({ open: true, type: 'reject', provider });
  };

  const confirmAction = async () => {
    const { type, provider } = actionDialog;
    try {
      await axios.put(`${API_BASE}/admin/provider/${provider._id}/kyc`, {
        kycStatus: type === 'approve' ? 'approved' : 'rejected',
        kycRejectReason: type === 'reject' ? rejectReason : undefined
      });
      fetchProviders();
      fetchStats();
    } catch (err) {
      setError('Error updating KYC: ' + err.message);
    }
    setActionDialog({ open: false, type: '', provider: null });
    setRejectReason('');
  };

  const filteredProviders = providers.filter(p => {
    const name = p.companyName || p.contactPerson || '';
    const phone = p.phone || p.mobile || '';
    const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase()) || phone.includes(searchQuery);
    return matchesSearch;
  });

  const handleDelete = async () => {
    const provider = deleteDialog.provider;
    try {
      await axios.delete(API_BASE + '/admin/provider/' + provider._id);
      setDeleteDialog({ open: false, provider: null });
      fetchProviders();
    } catch (err) {
      alert(err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Delete failed');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF5722' }}>
          Rojgar Provider KYC Management
        </Typography>
        <IconButton onClick={() => { fetchStats(); fetchProviders(); }} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'Total Providers', value: stats.total, color: '#2196F3' },
          { label: 'Pending KYC', value: stats.pending, color: '#FF9800' },
          { label: 'Approved', value: stats.approved, color: '#4CAF50' },
          { label: 'Rejected', value: stats.rejected, color: '#F44336' }
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ bgcolor: stat.color, color: 'white' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stat.value}</Typography>
                <Typography variant="body1">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by company name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>KYC Status</InputLabel>
              <Select value={statusFilter} label="KYC Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="submitted">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="outlined" startIcon={<Download />} fullWidth>Export</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead sx={{ bgcolor: '#FF5722' }}>
                <TableRow>
                  {['Company', 'Contact', 'Phone', 'Category', 'KYC Status', 'Created', 'Actions'].map(header => (
                    <TableCell key={header} sx={{ color: 'white', fontWeight: 'bold' }}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProviders.map((provider) => (
                  <TableRow key={provider._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: '#FF5722' }} src={provider.companyLogo}>
                          {(provider.companyName || 'P')[0]}
                        </Avatar>
                        <Box>
                          <Typography fontWeight="bold">{provider.companyName || 'N/A'}</Typography>
                          <Typography variant="caption" color="textSecondary">{provider.city}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{provider.contactPerson || 'N/A'}</TableCell>
                    <TableCell>{provider.phone || provider.mobile || 'N/A'}</TableCell>
                    <TableCell>{provider.category?.name || provider.businessType || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip label={provider.kycStatus || 'not_started'} color={getStatusColor(provider.kycStatus)} size="small" />
                    </TableCell>
                    <TableCell>{provider.createdAt ? new Date(provider.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => { setSelectedProvider(provider); setDetailDialog(true); }}>
                        <Visibility />
                      </IconButton>
                      {(provider.kycStatus === 'submitted' || provider.kycStatus === 'pending') && (
                        <>
                          <IconButton color="success" onClick={() => handleApprove(provider)}><Check /></IconButton>
                          <IconButton color="error" onClick={() => handleReject(provider)}><Close /></IconButton>
                        </>
                      )}
                      <IconButton color="error" onClick={() => setDeleteDialog({ open: true, provider })} size="small" title="Delete Provider"><DeleteOutline fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredProviders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No providers found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#FF5722', color: 'white' }}>
          Provider Details - {selectedProvider?.companyName}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedProvider && (
            <>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                <Tab label="Basic Info" />
                <Tab label="KYC Documents" />
              </Tabs>
              {tabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography><b>Company:</b> {selectedProvider.companyName}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Contact:</b> {selectedProvider.contactPerson}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Phone:</b> {selectedProvider.phone || selectedProvider.mobile}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Email:</b> {selectedProvider.email}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Business Type:</b> {selectedProvider.businessType}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>City:</b> {selectedProvider.city}, {selectedProvider.state}</Typography></Grid>
                  <Grid item xs={12}><Typography><b>Address:</b> {selectedProvider.address}</Typography></Grid>
                  <Grid item xs={6}>
                    <Typography><b>Status:</b> <Chip label={selectedProvider.kycStatus} color={getStatusColor(selectedProvider.kycStatus)} size="small" /></Typography>
                  </Grid>
                  <Grid item xs={6}><Typography><b>Verified:</b> {selectedProvider.isVerified ? 'Yes' : 'No'}</Typography></Grid>
                </Grid>
              )}
              {tabValue === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">GST Document</Typography>
                      {selectedProvider.gstDocument ? (
                        <Button href={selectedProvider.gstDocument} target="_blank" size="small" sx={{ mt: 1 }}>View</Button>
                      ) : (
                        <Chip label="Not Uploaded" color="warning" sx={{ mt: 1 }} />
                      )}
                      <Typography variant="caption" display="block">{selectedProvider.gstNumber || 'No GST Number'}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">PAN Document</Typography>
                      {selectedProvider.panDocument ? (
                        <Button href={selectedProvider.panDocument} target="_blank" size="small" sx={{ mt: 1 }}>View</Button>
                      ) : (
                        <Chip label="Not Uploaded" color="warning" sx={{ mt: 1 }} />
                      )}
                      <Typography variant="caption" display="block">{selectedProvider.panNumber || 'No PAN Number'}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">Address Proof</Typography>
                      {selectedProvider.addressProof ? (
                        <Button href={selectedProvider.addressProof} target="_blank" size="small" sx={{ mt: 1 }}>View</Button>
                      ) : (
                        <Chip label="Not Uploaded" color="warning" sx={{ mt: 1 }} />
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
          {(selectedProvider?.kycStatus === 'submitted' || selectedProvider?.kycStatus === 'pending') && (
            <>
              <Button variant="contained" color="success" onClick={() => { setDetailDialog(false); handleApprove(selectedProvider); }}>Approve</Button>
              <Button variant="contained" color="error" onClick={() => { setDetailDialog(false); handleReject(selectedProvider); }}>Reject</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, type: '', provider: null })}>
        <DialogTitle>{actionDialog.type === 'approve' ? 'Approve KYC' : 'Reject KYC'}</DialogTitle>
        <DialogContent>
          {actionDialog.type === 'approve' ? (
            <Typography>Are you sure you want to approve KYC for <b>{actionDialog.provider?.companyName}</b>?</Typography>
          ) : (
            <>
              <Typography sx={{ mb: 2 }}>Select reason for rejection:</Typography>
              <FormControl fullWidth>
                <InputLabel>Rejection Reason</InputLabel>
                <Select value={rejectReason} label="Rejection Reason" onChange={(e) => setRejectReason(e.target.value)}>
                  {rejectReasons.map(reason => (<MenuItem key={reason} value={reason}>{reason}</MenuItem>))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: '', provider: null })}>Cancel</Button>
          <Button variant="contained" color={actionDialog.type === 'approve' ? 'success' : 'error'} onClick={confirmAction} disabled={actionDialog.type === 'reject' && !rejectReason}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, provider: null })}>
        <DialogTitle>Delete Provider</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to <b>permanently delete</b> provider <b>{deleteDialog.provider?.companyName || deleteDialog.provider?.name}</b>? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, provider: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderKYC;
